import React from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { RoleName } from "constants/index";
import type { IClientEntityAllocation } from "store/types";

const MANAGE_ROLES = [RoleName.Admin, RoleName.Analyst];

// ------ Table Header -----------------------------------------------

const HEADER_CELLS = ["Entity name", "Linked deals", "Actions"] as const;

// ------ Color tokens -----------------------------------------------

const ACTION_ICON = {
  width: "24px",
  height: "24px",
  color: "#5e7a95",
  "&:hover": { bgcolor: "#f5f7f7" }
} as const;

const CELL_SX = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  padding: "0 20px",
  color: "#0a3142",
  fontSize: "16px",
  height: "48px"
} as const;

// ------ Row -----------------------------------------------

interface IEntityRowProps {
  entity: IClientEntityAllocation;
  onRemoveEntity: (entityId: number) => void;
}

const EntityRow: React.FC<IEntityRowProps> = ({ entity, onRemoveEntity }) => {
  const entityName = entity.name ?? "";

  return (
    <TableRow sx={{ "&:hover": { bgcolor: "action.hover" } }}>
      {/* Entity name + group + shared indicator */}
      <TableCell sx={CELL_SX}>
        <MaterialTooltip arrow={false} title={entityName} isShow variant="dark" placement="top-start">
          <Stack>
            <Typography
              sx={{
                fontSize: "16px",
                color: "#0a3142",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {entityName}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#898989" }}>{entity.group ?? "—"}</Typography>
          </Stack>
        </MaterialTooltip>
      </TableCell>

      {/* Linked deals count */}
      <TableCell align="center" sx={CELL_SX}>
        {entity.linked_deals_count}
      </TableCell>

      {/* Actions — remove entity (managing roles only) */}
      <TableCell align="center" sx={CELL_SX}>
        <RoleComponentGuard availableRoles={MANAGE_ROLES}>
          <Stack direction="row" sx={{ justifyContent: "center", gap: 0.5 }}>
            <IconButton onClick={() => onRemoveEntity(entity.id)} sx={ACTION_ICON}>
              <DeleteOutlineIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Stack>
        </RoleComponentGuard>
      </TableCell>
    </TableRow>
  );
};

export const EntitiesTable: React.FC<{
  entities: IClientEntityAllocation[];
  onRemoveEntity: (entityId: number) => void;
}> = ({ entities, onRemoveEntity }) => {
  return (
    <TableContainer sx={{ borderRadius: "10px", border: "1px solid #dae0e2", height: "100%", overflow: "auto" }}>
      <Table sx={{ minWidth: 900, color: "#0a3142" }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#edf1f9" }}>
            {HEADER_CELLS.map((header) => (
              <TableCell
                key={header}
                sx={{
                  fontSize: "15px",
                  fontWeight: "normal",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  padding: "0 20px",
                  color: "#0a3142",
                  height: "50px",
                  textAlign: header !== "Entity name" ? "center" : "flex-start"
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {entities.map((entity) => (
            <EntityRow key={entity.id} entity={entity} onRemoveEntity={onRemoveEntity} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
